import { httpClient } from "../../../lib/http/axios";
import type { ApiEnvelope } from "../../../lib/http/contracts";
import { verifyBusinessGeo } from "../../../shared/business-geo";
import {
  mapCrmOrganizationListParams,
  mapCrmOrganizationMutationPayload,
  mapCrmOrganizationSiteMutationPayload,
  type CrmOrganizationItemRaw,
  type CrmOrganizationListPageRaw,
  type CrmOrganizationSiteItemRaw,
} from "../api/adapters";
import type {
  CrmCampusDiscoverySummary,
  CrmOrganizationGeoResolvePayload,
  CrmOrganizationGeoResolveResult,
  CrmOrganizationItem,
  CrmOrganizationListPage,
  CrmOrganizationListParams,
  CrmOrganizationMutation,
  CrmOrganizationSiteItem,
  CrmOrganizationSiteMutation,
  CrmUnitDiscoverySummary,
} from "../types";

type CrmCampusDiscoveryCandidateRaw = {
  provider: "amap" | "amap_geocode";
  poi_id: string;
  name: string;
  address: string;
  province: string;
  city: string;
  district: string;
  longitude: number;
  latitude: number;
  is_qualified: boolean;
  qualification_reason: string;
};

type CrmCampusDiscoveryPendingItemRaw = {
  site_name: string;
  reason: string;
  province: string;
  city: string;
  district: string;
  address: string;
  candidates: CrmCampusDiscoveryCandidateRaw[];
};

type CrmCampusDiscoverySummaryRaw = {
  status: CrmCampusDiscoverySummary["status"];
  discovered_count: number;
  created_count: number;
  existing_count: number;
  pending_count: number;
  failure_reasons: string[];
  pending_items?: CrmCampusDiscoveryPendingItemRaw[];
  retryable: boolean;
};

type CrmUnitDiscoverySummaryRaw = {
  status: CrmUnitDiscoverySummary["status"];
  discovered_count: number;
  created_count: number;
  existing_count: number;
  failure_reasons: string[];
  retryable: boolean;
};

type CrmOrganizationItemWithDiscoveryRaw = CrmOrganizationItemRaw & {
  campus_discovery?: CrmCampusDiscoverySummaryRaw | null;
  unit_discovery?: CrmUnitDiscoverySummaryRaw | null;
};

/** 查询学校组织分页列表。 */
export async function fetchCrmOrganizationList(
  params: CrmOrganizationListParams,
): Promise<CrmOrganizationListPage> {
  const response = await httpClient.get<ApiEnvelope<CrmOrganizationListPageRaw>>(
    "/crm/organizations",
    {
      params: mapCrmOrganizationListParams(params),
    },
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("学校组织列表接口返回了无效数据");
  }
  return mapCrmOrganizationListPage(response.data.data);
}

/** 查询学校组织详情。 */
export async function fetchCrmOrganizationDetail(id: number): Promise<CrmOrganizationItem> {
  const response = await httpClient.get<ApiEnvelope<CrmOrganizationItemRaw>>(
    `/crm/organizations/${id}`,
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("学校组织详情接口返回了无效数据");
  }
  return mapCrmOrganizationItem(response.data.data);
}

/** 创建学校组织。 */
export async function createCrmOrganization(
  payload: CrmOrganizationMutation,
): Promise<CrmOrganizationItem> {
  const response = await httpClient.post<ApiEnvelope<CrmOrganizationItemRaw>>(
    "/crm/organizations",
    mapCrmOrganizationMutationPayload(payload),
    { timeout: 180_000, "axios-retry": { retries: 0 } },
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("学校组织创建接口返回了无效数据");
  }
  return mapCrmOrganizationItem(response.data.data);
}

/** 更新学校组织。 */
export async function updateCrmOrganization(
  id: number,
  payload: CrmOrganizationMutation,
): Promise<CrmOrganizationItem> {
  const response = await httpClient.put<ApiEnvelope<CrmOrganizationItemRaw>>(
    `/crm/organizations/${id}`,
    mapCrmOrganizationMutationPayload(payload),
    { timeout: 180_000, "axios-retry": { retries: 0 } },
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("学校组织更新接口返回了无效数据");
  }
  return mapCrmOrganizationItem(response.data.data);
}

/** 删除学校组织。 */
export async function deleteCrmOrganization(id: number): Promise<void> {
  await httpClient.delete(`/crm/organizations/${id}`);
}

/** 创建学校校区。 */
export async function createCrmOrganizationSite(
  organizationId: number,
  payload: CrmOrganizationSiteMutation,
): Promise<CrmOrganizationSiteItem> {
  const response = await httpClient.post<ApiEnvelope<CrmOrganizationSiteItemRaw>>(
    `/crm/organizations/${organizationId}/sites`,
    mapCrmOrganizationSiteMutationPayload(payload),
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("学校校区创建接口返回了无效数据");
  }
  return mapCrmOrganizationSiteItem(response.data.data);
}

/** 更新学校校区。 */
export async function updateCrmOrganizationSite(
  organizationId: number,
  siteId: number,
  payload: CrmOrganizationSiteMutation,
): Promise<CrmOrganizationSiteItem> {
  const response = await httpClient.put<ApiEnvelope<CrmOrganizationSiteItemRaw>>(
    `/crm/organizations/${organizationId}/sites/${siteId}`,
    mapCrmOrganizationSiteMutationPayload(payload),
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("学校校区更新接口返回了无效数据");
  }
  return mapCrmOrganizationSiteItem(response.data.data);
}

/** 删除学校校区。 */
export async function deleteCrmOrganizationSite(
  organizationId: number,
  siteId: number,
): Promise<void> {
  await httpClient.delete(`/crm/organizations/${organizationId}/sites/${siteId}`);
}

/** 调用 GEO 识别接口。 */
export async function resolveCrmOrganizationGeo(
  payload: CrmOrganizationGeoResolvePayload,
): Promise<CrmOrganizationGeoResolveResult> {
  const result = await verifyBusinessGeo({
    province: payload.province ?? "",
    city: payload.city,
    district: payload.district,
    areaName: payload.areaName,
    scenario: "school_campus",
    contextName: payload.contextName,
    sourceAddress: payload.sourceAddress,
    organizationSiteCount: payload.organizationSiteCount,
  });
  return {
    province: result.province,
    city: result.city,
    district: result.district,
    town: "",
    longitude: result.longitude,
    latitude: result.latitude,
    coordinateSystem: result.coordinateSystem,
    navigationAddress: result.amapStandardAddress,
    resolvedLevel: result.status === "confirmed" ? "area" : "unknown",
    confidenceScore: result.confidenceScore,
    needsReview: result.status !== "confirmed",
    message: result.correctionReason || result.conclusion,
    ambiguityNotes: result.llmConclusion,
    candidates: result.candidates,
  };
}
/** 发现并补齐高校尚未添加的校区；学校主档不参与本次事务，已存在校区按幂等键跳过。 */
export async function retryCrmOrganizationCampusDiscovery(
  organizationId: number,
): Promise<CrmCampusDiscoverySummary> {
  const response = await httpClient.post<ApiEnvelope<CrmCampusDiscoverySummaryRaw>>(
    `/crm/organizations/${organizationId}/campus-discovery`,
    {},
    {
      timeout: 600_000,
      "axios-retry": { retries: 0 },
    },
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("高校校区发现接口返回了无效数据");
  }
  return mapCampusDiscoverySummary(response.data.data);
}

/** 异步发现并幂等写入高校艺术相关组织单元。 */
export async function discoverCrmOrganizationArtUnits(
  organizationId: number,
): Promise<CrmUnitDiscoverySummary> {
  const response = await httpClient.post<ApiEnvelope<CrmUnitDiscoverySummaryRaw>>(
    `/crm/organizations/${organizationId}/art-unit-discovery`,
    {},
    {
      timeout: 600_000,
      "axios-retry": { retries: 0 },
    },
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("艺术相关组织单元发现接口返回了无效数据");
  }
  return mapUnitDiscoverySummary(response.data.data);
}

/** 映射学校组织分页响应。 */
function mapCrmOrganizationListPage(raw: CrmOrganizationListPageRaw): CrmOrganizationListPage {
  return {
    items: (raw.items ?? []).map(mapCrmOrganizationItem),
    page: raw.page,
    pageSize: raw.page_size,
    total: raw.total,
  };
}

/** 映射学校组织响应项。 */
function mapCrmOrganizationItem(raw: CrmOrganizationItemRaw): CrmOrganizationItem {
  const rawWithDiscovery = raw as CrmOrganizationItemWithDiscoveryRaw;
  return {
    vectorSyncWarning: raw.vector_sync_warning ?? undefined,
    id: raw.id,
    legacyId: raw.legacy_id,
    orgName: raw.org_name,
    orgNameNormalized: raw.org_name_normalized,
    orgType: raw.org_type,
    orgSubtype: raw.org_subtype ?? undefined,
    spellCode: raw.spell_code,
    province: raw.province ?? undefined,
    city: raw.city ?? undefined,
    district: raw.district ?? undefined,
    address: raw.address ?? undefined,
    isDisabled: raw.is_disabled,
    isDisabledLabel: raw.is_disabled_label,
    createdAt: raw.created_at,
    updatedAt: raw.updated_at,
    remark: raw.remark ?? undefined,
    mainSiteName: raw.main_site_name ?? undefined,
    siteCount: raw.site_count,
    sites: (raw.sites ?? []).map(mapCrmOrganizationSiteItem),
    campusDiscovery: rawWithDiscovery.campus_discovery
      ? mapCampusDiscoverySummary(rawWithDiscovery.campus_discovery)
      : undefined,
    unitDiscovery: rawWithDiscovery.unit_discovery
      ? mapUnitDiscoverySummary(rawWithDiscovery.unit_discovery)
      : undefined,
  };
}

/** 映射高校自动校区发现汇总。 */
function mapCampusDiscoverySummary(
  raw: CrmCampusDiscoverySummaryRaw,
): CrmCampusDiscoverySummary {
  return {
    status: raw.status,
    discoveredCount: raw.discovered_count,
    createdCount: raw.created_count,
    existingCount: raw.existing_count,
    pendingCount: raw.pending_count,
    failureReasons: raw.failure_reasons ?? [],
    pendingItems: (raw.pending_items ?? []).map((item) => ({
      siteName: item.site_name,
      reason: item.reason,
      province: item.province ?? "",
      city: item.city ?? "",
      district: item.district ?? "",
      address: item.address ?? "",
      candidates: (item.candidates ?? []).map((candidate) => ({
        provider: candidate.provider,
        poiId: candidate.poi_id,
        name: candidate.name,
        address: candidate.address ?? "",
        province: candidate.province ?? "",
        city: candidate.city ?? "",
        district: candidate.district ?? "",
        longitude: candidate.longitude,
        latitude: candidate.latitude,
        isQualified: candidate.is_qualified,
        qualificationReason: candidate.qualification_reason ?? "",
      })),
    })),
    retryable: raw.retryable,
  };
}

/** 映射艺术相关组织单元自动发现汇总。 */
function mapUnitDiscoverySummary(raw: CrmUnitDiscoverySummaryRaw): CrmUnitDiscoverySummary {
  return {
    status: raw.status,
    discoveredCount: raw.discovered_count,
    createdCount: raw.created_count,
    existingCount: raw.existing_count,
    failureReasons: raw.failure_reasons ?? [],
    retryable: raw.retryable,
  };
}

/** 映射学校校区响应项。 */
function mapCrmOrganizationSiteItem(raw: CrmOrganizationSiteItemRaw): CrmOrganizationSiteItem {
  return {
    id: raw.id,
    orgId: raw.org_id,
    siteName: raw.site_name,
    siteType: raw.site_type ?? undefined,
    province: raw.province ?? undefined,
    city: raw.city ?? undefined,
    district: raw.district ?? undefined,
    address: raw.address,
    lng: raw.lng === null || raw.lng === undefined ? undefined : Number(raw.lng),
    lat: raw.lat === null || raw.lat === undefined ? undefined : Number(raw.lat),
    geoStatus: raw.geo_status === "confirmed" ? "confirmed" : "unverified",
    isMainSite: raw.is_main_site,
    isMainSiteLabel: raw.is_main_site_label,
    isDisabled: raw.is_disabled,
    isDisabledLabel: raw.is_disabled_label,
    createdAt: raw.created_at,
    updatedAt: raw.updated_at,
    remark: raw.remark ?? undefined,
  };
}
